import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  FileDown,
  Plus,
  Search,
  Trash2,
  Warehouse,
} from "lucide-react";

import { ApiError, formatDateTime } from "../../lib/api";
import { coincideBusqueda } from "./filtros";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  type BadgeTone,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarUbicacionExistencia,
  anularMovimiento,
  crearBodega,
  existenciasDeBodega,
  exportarExistencias,
  listarAlertas,
  listarBodegas,
  listarInsumos,
  listarMovimientos,
  registrarMovimiento,
  type BodegaCreate,
  type BodegaRead,
  type InsumoRead,
  type MovimientoRead,
  type RegistrarMovimientoRequest,
  type TipoBodega,
  type TipoMovimiento,
} from "./api";

const TIPOS: { value: TipoMovimiento; label: string }[] = [
  { value: "ENTRADA", label: "Entrada (compra / ingreso)" },
  { value: "SALIDA", label: "Salida (consumo)" },
  { value: "TRASPASO", label: "Traspaso entre bodegas" },
  { value: "AJUSTE", label: "Ajuste de inventario" },
];

const MOTIVOS_POR_TIPO: Record<TipoMovimiento, string[]> = {
  ENTRADA: ["Compra", "Devolucion"],
  SALIDA: ["ConsumoOT", "Devolucion", "AjusteInventario"],
  TRASPASO: ["Traspaso"],
  AJUSTE: ["AjusteInventario"],
};

const TIPO_TONE: Record<TipoMovimiento, BadgeTone> = {
  ENTRADA: "emerald",
  SALIDA: "amber",
  TRASPASO: "brand",
  AJUSTE: "slate",
};

export function BodegaPage(): JSX.Element {
  const [bodegaSel, setBodegaSel] = useState<number | null>(null);
  const [registrando, setRegistrando] = useState(false);
  const [nuevaBodega, setNuevaBodega] = useState(false);
  const [anulando, setAnulando] = useState<MovimientoRead | null>(null);
  const [qExistencias, setQExistencias] = useState("");
  const [tipoMov, setTipoMov] = useState<TipoMovimiento | "">("");
  const toastExport = useToast();

  const bodegasQ = useQuery({ queryKey: ["inventario", "bodegas"], queryFn: listarBodegas });
  const insumosQ = useQuery({ queryKey: ["inventario", "insumos"], queryFn: listarInsumos });
  const alertasQ = useQuery({ queryKey: ["inventario", "alertas"], queryFn: listarAlertas });
  const movimientosQ = useQuery({
    queryKey: ["inventario", "movimientos"],
    queryFn: () => listarMovimientos({ limit: 50 }),
  });

  const bodegaActual = bodegaSel ?? bodegasQ.data?.[0]?.id ?? null;

  const existenciasQ = useQuery({
    queryKey: ["inventario", "existencias", bodegaActual],
    queryFn: () => existenciasDeBodega(bodegaActual as number),
    enabled: bodegaActual !== null,
  });

  const insumoNombre = useMemo(() => {
    const m = new Map<number, string>();
    for (const i of insumosQ.data ?? []) m.set(i.id, i.nombre);
    return m;
  }, [insumosQ.data]);

  const bodegaNombre = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of bodegasQ.data ?? []) m.set(b.id, b.nombre);
    return m;
  }, [bodegasQ.data]);

  const existenciasFiltradas = useMemo(
    () =>
      (existenciasQ.data ?? []).filter((e) =>
        coincideBusqueda(qExistencias, e.insumo_nombre, e.ubicacion_estante),
      ),
    [existenciasQ.data, qExistencias],
  );

  const movimientosFiltrados = useMemo(
    () =>
      (movimientosQ.data ?? []).filter((m) => !tipoMov || m.tipo === tipoMov),
    [movimientosQ.data, tipoMov],
  );

  const sinBodegas = bodegasQ.data && bodegasQ.data.length === 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bodega"
        icon={Warehouse}
        actions={
          <>
            <Button
              variant="secondary"
              icon={FileDown}
              disabled={sinBodegas}
              onClick={() => {
                exportarExistencias().catch((e: unknown) =>
                  toastExport.error(e instanceof ApiError ? e.message : "No se pudo exportar"),
                );
              }}
            >
              Existencias valorizadas
            </Button>
            <Button variant="secondary" icon={Plus} onClick={() => setNuevaBodega(true)}>
              Nueva bodega
            </Button>
            <Button
              icon={Plus}
              onClick={() => setRegistrando(true)}
              disabled={sinBodegas}
            >
              Registrar movimiento
            </Button>
          </>
        }
      />

      {bodegasQ.isPending && <Cargando />}
      {bodegasQ.isError && (
        <ErrorCarga error={bodegasQ.error} onReintentar={() => bodegasQ.refetch()} />
      )}

      {sinBodegas && (
        <EmptyState
          icon={Warehouse}
          title="No hay bodegas todavía"
          description="Crea al menos una bodega (la central, o la camioneta del operador) para empezar a manejar stock."
        >
          <Button icon={Plus} onClick={() => setNuevaBodega(true)}>
            Nueva bodega
          </Button>
        </EmptyState>
      )}

      {/* Alertas de reposición */}
      {alertasQ.data && alertasQ.data.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="mb-1.5 flex items-center gap-2 text-sm font-medium text-amber-900">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.5} />
            Reposición pendiente ({alertasQ.data.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {alertasQ.data.map((a) => (
              <Badge key={a.insumo_id} tone={a.nivel === "critico" ? "rose" : "amber"}>
                {a.insumo_nombre}: {Number(a.cantidad_total)} {a.unidad_medida}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Selector de bodega + existencias */}
      {bodegasQ.data && !sinBodegas && (
        <section className="rounded-lg bg-white p-4 ring-1 ring-neutral-200">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-neutral-700">Bodega</label>
            <select
              value={bodegaActual ?? ""}
              onChange={(e) => setBodegaSel(Number(e.target.value))}
              className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            >
              {(bodegasQ.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nombre}
                  {b.tipo === "Movil" ? " (móvil)" : ""}
                </option>
              ))}
            </select>
            {(existenciasQ.data?.length ?? 0) > 0 && (
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                <input
                  type="text"
                  value={qExistencias}
                  onChange={(e) => setQExistencias(e.target.value)}
                  placeholder="Buscar insumo…"
                  className="w-56 rounded-md border border-neutral-450 py-1.5 pl-8 pr-3 text-sm shadow-sm"
                />
              </div>
            )}
          </div>

          {existenciasQ.isPending ? (
            <Cargando inline />
          ) : existenciasQ.isError ? (
            <ErrorCarga
              error={existenciasQ.error}
              onReintentar={() => existenciasQ.refetch()}
            />
          ) : existenciasQ.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-500">
              Esta bodega no tiene existencias todavía. Registra una entrada para cargar stock.
            </p>
          ) : existenciasFiltradas.length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-500">
              Ningún insumo coincide con la búsqueda.
            </p>
          ) : (
            <TableWrap label="Existencias de la bodega">
              <table className="min-w-full divide-y divide-neutral-200 text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">Insumo</th>
                    <th scope="col" className="px-3 py-2 text-right">Cantidad</th>
                    <th scope="col" className="px-3 py-2 text-left">Ubicación</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {existenciasFiltradas.map((e) => (
                    <tr key={`${e.bodega_id}-${e.insumo_id}`} className="hover:bg-neutral-50">
                      <td className="px-3 py-2 font-medium text-neutral-900">{e.insumo_nombre}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {Number(e.cantidad)}{" "}
                        <span className="text-xs text-neutral-500">{e.unidad_medida}</span>
                      </td>
                      <td className="px-3 py-2 text-neutral-600">
                        <UbicacionCell
                          bodegaId={e.bodega_id}
                          insumoId={e.insumo_id}
                          valor={e.ubicacion_estante}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </section>
      )}

      {/* Historial de movimientos */}
      {movimientosQ.data && movimientosQ.data.length > 0 && (
        <section className="overflow-hidden rounded-lg bg-white ring-1 ring-neutral-200">
          <div className="flex items-center gap-3 border-b border-neutral-100 px-4 py-2">
            <span className="text-sm font-medium text-neutral-700">
              Últimos movimientos
            </span>
            <select
              value={tipoMov}
              onChange={(e) => setTipoMov(e.target.value as TipoMovimiento | "")}
              className="ml-auto rounded-md border border-neutral-450 px-3 py-1 text-sm shadow-sm"
            >
              <option value="">Todos los tipos</option>
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.value}
                </option>
              ))}
            </select>
          </div>
          {movimientosFiltrados.length === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-500">
              No hay movimientos de este tipo.
            </p>
          ) : (
          <div className="overflow-x-auto" role="region" aria-label="Últimos movimientos" tabIndex={0}>
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Fecha</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                <th scope="col" className="px-3 py-2 text-left">Motivo</th>
                <th scope="col" className="px-3 py-2 text-left">Bodegas</th>
                <th scope="col" className="px-3 py-2 text-left">Insumos</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {movimientosFiltrados.map((m) => (
                <tr key={m.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 whitespace-nowrap text-neutral-600">
                    {formatDateTime(m.fecha)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={TIPO_TONE[m.tipo]}>{m.tipo}</Badge>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">{m.motivo}</td>
                  <td className="px-3 py-2 text-neutral-600">
                    <span className="inline-flex items-center gap-1 text-xs">
                      {m.bodega_origen_id != null && (bodegaNombre.get(m.bodega_origen_id) ?? "?")}
                      {m.bodega_origen_id != null && m.bodega_destino_id != null && (
                        <ArrowRight className="h-3 w-3 text-neutral-500" />
                      )}
                      {m.bodega_destino_id != null && (bodegaNombre.get(m.bodega_destino_id) ?? "?")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-500">
                    {m.detalles
                      .map((d) => `${insumoNombre.get(d.insumo_id) ?? `#${d.insumo_id}`} ×${Number(d.cantidad)}`)
                      .join(", ")}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={m.estado === "Anulado" ? "slate" : "emerald"}>{m.estado}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {m.estado === "Confirmado" && m.motivo !== "Reversa" && (
                      <Button
                        variant="linkDanger"
                        size="sm"
                        icon={Ban}
                        onClick={() => setAnulando(m)}
                      >
                        Anular
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          )}
        </section>
      )}

      {nuevaBodega && <BodegaFormModal onClose={() => setNuevaBodega(false)} />}

      {registrando && (
        <MovimientoModal
          bodegas={bodegasQ.data ?? []}
          insumos={insumosQ.data ?? []}
          onClose={() => setRegistrando(false)}
        />
      )}

      {anulando && (
        <AnularModal movimiento={anulando} onClose={() => setAnulando(null)} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Celda de ubicación editable (estante/pasillo del insumo en la bodega)
// ────────────────────────────────────────────────────────────────────────

function UbicacionCell({
  bodegaId,
  insumoId,
  valor,
}: {
  bodegaId: number;
  insumoId: number;
  valor: string | null;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(valor ?? "");
  const cancelar = useRef(false);

  const mut = useMutation({
    mutationFn: (v: string | null) =>
      actualizarUbicacionExistencia(bodegaId, insumoId, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inventario", "existencias"] });
      setEditando(false);
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo guardar la ubicación",
      );
      setEditando(false);
    },
  });

  function abrir(): void {
    setTexto(valor ?? "");
    cancelar.current = false;
    setEditando(true);
  }

  function guardarSiCambio(): void {
    if (cancelar.current) {
      cancelar.current = false;
      setEditando(false);
      return;
    }
    const limpio = texto.trim();
    if (limpio === (valor ?? "")) {
      setEditando(false);
      return;
    }
    mut.mutate(limpio || null);
  }

  if (!editando) {
    return (
      <button
        type="button"
        onClick={abrir}
        className="-mx-1 rounded px-1 text-left hover:bg-neutral-100"
        title="Editar ubicación"
      >
        {valor ? valor : <span className="text-neutral-500">Asignar…</span>}
      </button>
    );
  }

  return (
    <input
      autoFocus
      type="text"
      maxLength={50}
      value={texto}
      disabled={mut.isPending}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={guardarSiCambio}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        else if (e.key === "Escape") {
          cancelar.current = true;
          e.currentTarget.blur();
        }
      }}
      placeholder="ej. Pasillo 2, Repisa B"
      className="w-40 rounded-md border border-neutral-450 px-2 py-1 text-sm shadow-sm"
    />
  );
}

// ────────────────────────────────────────────────────────────────────────
// Nueva bodega
// ────────────────────────────────────────────────────────────────────────

function BodegaFormModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState<TipoBodega>("Central");
  const [ubicacion, setUbicacion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (req: BodegaCreate) => crearBodega(req),
    onSuccess: () => {
      toast.success("Bodega creada");
      void qc.invalidateQueries({ queryKey: ["inventario", "bodegas"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear la bodega";
      setError(msg);
      toast.error(msg);
    },
  });

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Pon un nombre para la bodega");
    mut.mutate({
      nombre: nombre.trim(),
      tipo,
      ...(ubicacion.trim() ? { ubicacion: ubicacion.trim() } : {}),
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([nombre, tipo, ubicacion]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva bodega"
      icon={Warehouse}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? "Creando…" : "Crear bodega"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Nombre"
          required
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          autoFocus
          placeholder="Bodega central"
        />
        <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoBodega)}>
          <option value="Central">Central (fija)</option>
          <option value="Movil">Móvil (camioneta del operador)</option>
        </Select>
        <Input
          label="Ubicación (opcional)"
          type="text"
          value={ubicacion}
          onChange={(e) => setUbicacion(e.target.value)}
          placeholder="Dirección o referencia"
        />
        {error && (
          <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Registrar movimiento
// ────────────────────────────────────────────────────────────────────────

interface LineaForm {
  insumoId: string;
  cantidad: string;
  costoUnitario: string;
}

function MovimientoModal({
  bodegas,
  insumos,
  onClose,
}: {
  bodegas: BodegaRead[];
  insumos: InsumoRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [tipo, setTipo] = useState<TipoMovimiento>("ENTRADA");
  const [motivo, setMotivo] = useState<string>("Compra");
  const [origenId, setOrigenId] = useState<string>("");
  const [destinoId, setDestinoId] = useState<string>("");
  const [ajusteDir, setAjusteDir] = useState<"sobrante" | "merma">("sobrante");
  const [observacion, setObservacion] = useState("");
  const [lineas, setLineas] = useState<LineaForm[]>([
    { insumoId: "", cantidad: "", costoUnitario: "" },
  ]);
  const [error, setError] = useState<string | null>(null);

  const insumosActivos = insumos.filter((i) => i.activo);
  const esEntrada = tipo === "ENTRADA";

  function cambiarTipo(nuevo: TipoMovimiento): void {
    setTipo(nuevo);
    setMotivo(MOTIVOS_POR_TIPO[nuevo][0] ?? "");
    setError(null);
  }

  const mut = useMutation({
    mutationFn: (req: RegistrarMovimientoRequest) => registrarMovimiento(req),
    onSuccess: () => {
      toast.success("Movimiento registrado");
      void qc.invalidateQueries({ queryKey: ["inventario"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo registrar";
      setError(msg);
      toast.error(msg);
    },
  });

  function submit(): void {
    setError(null);
    const detalles = [];
    for (const l of lineas) {
      const insumoId = Number(l.insumoId);
      const cantidad = Number(l.cantidad);
      if (!insumoId) return setError("Elige el insumo en cada línea");
      if (!Number.isFinite(cantidad) || cantidad <= 0)
        return setError("La cantidad de cada línea debe ser mayor a 0");
      detalles.push({
        insumo_id: insumoId,
        cantidad,
        ...(esEntrada ? { costo_unitario: Number(l.costoUnitario) || 0 } : {}),
      });
    }
    if (detalles.length === 0) return setError("Agrega al menos una línea");

    const req: RegistrarMovimientoRequest = { tipo, motivo, detalles };
    if (tipo === "ENTRADA") {
      if (!destinoId) return setError("Elige la bodega de destino");
      req.bodega_destino_id = Number(destinoId);
    } else if (tipo === "SALIDA") {
      if (!origenId) return setError("Elige la bodega de origen");
      req.bodega_origen_id = Number(origenId);
    } else if (tipo === "TRASPASO") {
      if (!origenId || !destinoId) return setError("Elige bodega de origen y destino");
      if (origenId === destinoId) return setError("Origen y destino deben ser distintos");
      req.bodega_origen_id = Number(origenId);
      req.bodega_destino_id = Number(destinoId);
    } else {
      // AJUSTE
      if (!origenId) return setError("Elige la bodega");
      if (ajusteDir === "sobrante") req.bodega_destino_id = Number(origenId);
      else req.bodega_origen_id = Number(origenId);
    }
    if (observacion.trim()) req.observacion = observacion.trim();
    mut.mutate(req);
  }

  const opcionesBodega = bodegas.map((b) => (
    <option key={b.id} value={b.id}>
      {b.nombre}
      {b.tipo === "Movil" ? " (móvil)" : ""}
    </option>
  ));

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    tipo, motivo, origenId, destinoId, ajusteDir, observacion, lineas,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Registrar movimiento"
      icon={Warehouse}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? "Registrando…" : "Registrar"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Select label="Tipo" value={tipo} onChange={(e) => cambiarTipo(e.target.value as TipoMovimiento)}>
            {TIPOS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          <Select label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            {(MOTIVOS_POR_TIPO[tipo] ?? []).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </div>

        {/* Bodegas según tipo */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(tipo === "SALIDA" || tipo === "TRASPASO") && (
            <Select label="Bodega origen" value={origenId} onChange={(e) => setOrigenId(e.target.value)}>
              <option value="">— Elegir —</option>
              {opcionesBodega}
            </Select>
          )}
          {(tipo === "ENTRADA" || tipo === "TRASPASO") && (
            <Select label="Bodega destino" value={destinoId} onChange={(e) => setDestinoId(e.target.value)}>
              <option value="">— Elegir —</option>
              {opcionesBodega}
            </Select>
          )}
          {tipo === "AJUSTE" && (
            <>
              <Select label="Bodega" value={origenId} onChange={(e) => setOrigenId(e.target.value)}>
                <option value="">— Elegir —</option>
                {opcionesBodega}
              </Select>
              <Select
                label="Tipo de ajuste"
                value={ajusteDir}
                onChange={(e) => setAjusteDir(e.target.value as "sobrante" | "merma")}
              >
                <option value="sobrante">Sobrante (suma al stock)</option>
                <option value="merma">Merma (resta del stock)</option>
              </Select>
            </>
          )}
        </div>

        {/* Líneas */}
        <div>
          <div className="mb-1 text-sm font-medium text-neutral-700">Insumos</div>
          <div className="space-y-2">
            {lineas.map((l, idx) => (
              <div key={idx} className="flex items-end gap-2">
                <div className="flex-1">
                  <Select
                    label={idx === 0 ? "Insumo" : ""}
                    value={l.insumoId}
                    onChange={(e) => {
                      const next = [...lineas];
                      next[idx] = { ...next[idx]!, insumoId: e.target.value };
                      setLineas(next);
                    }}
                  >
                    <option value="">— Elegir insumo —</option>
                    {insumosActivos.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.nombre} ({i.unidad_medida})
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="w-24">
                  <Input
                    label={idx === 0 ? "Cantidad" : ""}
                    type="number"
                    min={0}
                    value={l.cantidad}
                    onChange={(e) => {
                      const next = [...lineas];
                      next[idx] = { ...next[idx]!, cantidad: e.target.value };
                      setLineas(next);
                    }}
                  />
                </div>
                {esEntrada && (
                  <div className="w-32">
                    <Input
                      label={idx === 0 ? "Costo unit. $" : ""}
                      type="number"
                      min={0}
                      value={l.costoUnitario}
                      onChange={(e) => {
                        const next = [...lineas];
                        next[idx] = { ...next[idx]!, costoUnitario: e.target.value };
                        setLineas(next);
                      }}
                    />
                  </div>
                )}
                <Button
                  variant="linkDanger"
                  size="sm"
                  icon={Trash2}
                  onClick={() => setLineas(lineas.filter((_, i) => i !== idx))}
                  disabled={lineas.length === 1}
                >
                  {""}
                </Button>
              </div>
            ))}
          </div>
          <Button
            variant="link"
            size="sm"
            icon={Plus}
            onClick={() => setLineas([...lineas, { insumoId: "", cantidad: "", costoUnitario: "" }])}
            className="mt-1"
          >
            Agregar línea
          </Button>
        </div>

        {esEntrada && (
          <p className="text-xs text-neutral-500">
            El costo unitario recalcula el precio promedio (PPP) del insumo. Déjalo en 0 si no lo
            conoces.
          </p>
        )}

        <Input
          label="Observación (opcional)"
          type="text"
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
        />

        {error && (
          <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Anular
// ────────────────────────────────────────────────────────────────────────

function AnularModal({
  movimiento,
  onClose,
}: {
  movimiento: MovimientoRead;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => anularMovimiento(movimiento.id, motivo.trim()),
    onSuccess: () => {
      toast.success("Movimiento anulado (se generó la reversa)");
      void qc.invalidateQueries({ queryKey: ["inventario"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo anular";
      setError(msg);
      toast.error(msg);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Anular movimiento #${movimiento.id}`}
      subtitle={`${movimiento.tipo} · ${movimiento.motivo}`}
      icon={Ban}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (!motivo.trim()) return setError("Indica el motivo de la anulación");
              mut.mutate();
            }}
            disabled={mut.isPending}
          >
            {mut.isPending ? "Anulando…" : "Anular movimiento"}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-neutral-600">
        No se borra: se genera un contra-movimiento de reversa que revierte el stock. El original
        queda marcado como anulado para la auditoría.
      </p>
      <Input
        label="Motivo de la anulación"
        required
        type="text"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        autoFocus
      />
      {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
    </Modal>
  );
}
